import React from 'react';
import { useTranslation } from 'react-i18next';

import { IconButton, Text, Tooltip } from '@fly/core';
import { Skeleton } from '@radix-ui/themes';
import classNames from 'classnames';

import styles from '../../FlyChat.module.less';
import { FlyChatHistoryPopover } from '../FlyChatHistory/FlyChatHistory';

interface FlyChatHeaderProps {
  onNewChat: () => void;
  onClose?: () => void;
  isStreaming: boolean;
  isThreadLoading?: boolean;
  isChatEmpty: boolean;
  title?: string;
  currentThreadId?: string;
  onSelectThread: (threadId: string) => void;
  onDeleteThread: (threadId: string) => void;
}

export const FlyChatHeader: React.FC<FlyChatHeaderProps> = ({
  onNewChat,
  onClose,
  isStreaming = false,
  isThreadLoading = false,
  isChatEmpty = false,
  title,
  currentThreadId,
  onSelectThread,
  onDeleteThread,
}) => {
  const { t } = useTranslation();

  return (
    <div className={classNames(styles.centerItems, styles.headerWrapper)}>
      <Text data-testid='chat-title' size='2' className='flex-1 truncate'>
        {isThreadLoading ? (
          <Skeleton className='h-4 w-24' />
        ) : (
          title ||
          t('app.dashboard.chat.header.title', { defaultValue: 'New Chat' })
        )}
      </Text>
      <div className={styles.centerItems}>
        <Tooltip
          content={t('app.dashboard.chat.start_new_chat_tooltip', {
            defaultValue: 'Start New Chat',
          })}
          side='bottom'
        >
          <IconButton
            data-testid='start-new-chat-btn'
            aria-label={t('app.dashboard.chat.start_new_chat_tooltip', {
              defaultValue: 'Start New Chat',
            })}
            size='1'
            color='neutral'
            variant='ghost'
            onClick={onNewChat}
            icon='plus'
            disabled={isChatEmpty || isStreaming}
          />
        </Tooltip>
        <FlyChatHistoryPopover
          onSelectThread={onSelectThread}
          onNewChat={onNewChat}
          isChatEmpty={isChatEmpty}
          currentThreadId={currentThreadId}
          onDeleteThread={onDeleteThread}
        />
        <Tooltip
          content={t('app.dashboard.chat.close_tooltip', {
            defaultValue: 'Minimize Chat',
          })}
          side='bottom'
        >
          <IconButton
            data-testid='chat-close-btn'
            aria-label={t('app.dashboard.chat.close_tooltip', {
              defaultValue: 'Minimize Chat',
            })}
            size='1'
            color='neutral'
            variant='ghost'
            onClick={onClose}
            icon='minus'
          />
        </Tooltip>
      </div>
    </div>
  );
};
